/**
 * EnvelopeGate - Prevents audio clicks and buzzing
 *
 * Wraps note triggering with proper amplitude envelope gating:
 * - Fade-in on attack prevents start clicks
 * - Fade-out before release prevents end clicks
 * - Gap between notes prevents overlap buzzing
 * - Configurable attack/release ramp times
 */

export interface EnvelopeGateConfig {
  /** Attack ramp time in seconds (default: 0.003 = 3ms) */
  attackRamp: number;
  /** Release ramp time in seconds (default: 0.01 = 10ms) */
  releaseRamp: number;
  /** Gap between notes as fraction of note duration (default: 0.1 = 10%) */
  noteGap: number;
  /** Minimum gap in seconds (floor so short notes still have gap) */
  minGap: number;
}

const DEFAULT_CONFIG: EnvelopeGateConfig = {
  attackRamp: 0.003, // 3ms attack ramp
  releaseRamp: 0.01, // 10ms release ramp
  noteGap: 0.1, // 10% gap between notes
  minGap: 0.005, // 5ms minimum gap
};

export class EnvelopeGate {
  private config: EnvelopeGateConfig;

  constructor(config: Partial<EnvelopeGateConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Calculate the effective note duration with gap applied
   * Ensures notes don't run into each other causing clicks
   */
  getEffectiveDuration(originalDurationSeconds: number): number {
    const gap = Math.max(
      this.config.minGap,
      originalDurationSeconds * this.config.noteGap
    );
    // Ensure we don't make duration negative
    return Math.max(
      this.config.attackRamp + this.config.releaseRamp,
      originalDurationSeconds - gap
    );
  }

  /**
   * Get the attack ramp time
   */
  getAttackRamp(): number {
    return this.config.attackRamp;
  }

  /**
   * Get the release ramp time
   */
  getReleaseRamp(): number {
    return this.config.releaseRamp;
  }

  /**
   * Get the note gap fraction
   */
  getNoteGap(): number {
    return this.config.noteGap;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<EnvelopeGateConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Check if a duration is too short for proper envelope
   * (shorter than attack + release ramp time)
   */
  isTooShort(durationSeconds: number): boolean {
    // Use small epsilon for floating-point comparison
    const minEnvelope = this.config.attackRamp + this.config.releaseRamp;
    return durationSeconds < minEnvelope - 1e-10;
  }

  /**
   * Get the minimum duration that can be properly enveloped
   */
  getMinDuration(): number {
    return this.config.attackRamp + this.config.releaseRamp + this.config.minGap;
  }
}
